import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useReducer } from 'react';
import { noop } from 'lodash-es';
import {
  APPLY_AUTO_ROUTE_APPLIED_TEXT,
  APPLY_AUTO_ROUTE_COMPLETED_LEGS_TEXT,
  APPLY_AUTO_ROUTE_MISSING_RESOURCES_TEXT,
  APPLY_AUTO_ROUTE_STATUS_TEXT,
  PARTIAL_SUGGESTED_ROUTE_TYPES,
} from '@lp/features/RoutePlanner/config';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { getAffectedRouteLegs, getReplacedSegmentLegs, hasUnremovableLegs } from '@lp/features/RoutePlanner/utils';
import { CaseStatus } from '@/api/apis/case';
import { RouteTypeEnum } from '@/api/apis/route';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { isAllowedStatus } from '@/utils/helpers';
import { getResourceValidationError } from './SuggestedSettings/utils/validation';

type SuggestedRouteValidationContextType = {
  isValidRoute: boolean;
  isRegenerateAllowed: boolean;
  regenerateErrorMessages: string[];
  errorMessages: string[];
  setCSResource: (routeId: string, value: boolean) => void;
  setOCSResource: (routeId: string, value: boolean) => void;
  setRSResource: (routeId: string, value: boolean) => void;
};

const DEFAULT_SUGGESTED_ROUTE_VALIDATION_CONTEXT: SuggestedRouteValidationContextType = {
  isValidRoute: false,
  isRegenerateAllowed: false,
  regenerateErrorMessages: [],
  errorMessages: [],
  setCSResource: noop,
  setOCSResource: noop,
  setRSResource: noop,
};

export const SuggestedRouteValidationContext = createContext<SuggestedRouteValidationContextType>(
  DEFAULT_SUGGESTED_ROUTE_VALIDATION_CONTEXT,
);

type ResourceKey = 'cs' | 'ocs' | 'rs';

type ResourceAvailability = Partial<Record<ResourceKey, boolean>>;

type AvailabilityAction = {
  routeId: string;
  resource: ResourceKey;
  isAvailable: boolean;
};

const availabilityReducer = (
  state: Map<string, ResourceAvailability>,
  { routeId, resource, isAvailable }: AvailabilityAction,
): Map<string, ResourceAvailability> => {
  const current = state.get(routeId);

  if (current?.[resource] === isAvailable) return state;

  const nextState = new Map(state);
  nextState.set(routeId, { ...current, [resource]: isAvailable });

  return nextState;
};

type BlockingConditions = {
  isApplied: boolean;
  hasUnremovableRouteLegs: boolean;
  isRoutePlanningStatus: boolean;
  hasResources: boolean;
};

const getBlockingReason = ({
  isApplied,
  hasUnremovableRouteLegs,
  isRoutePlanningStatus,
  hasResources,
}: BlockingConditions): string | null => {
  if (isApplied) return APPLY_AUTO_ROUTE_APPLIED_TEXT;
  if (hasUnremovableRouteLegs) return APPLY_AUTO_ROUTE_COMPLETED_LEGS_TEXT;
  if (!isRoutePlanningStatus) return APPLY_AUTO_ROUTE_STATUS_TEXT;
  if (!hasResources) return APPLY_AUTO_ROUTE_MISSING_RESOURCES_TEXT;

  return null;
};

type SuggestedRouteValidationProviderProps = {
  suggestedRouteId?: string;
  /** Permission, plan validity and loading already resolved by the caller. */
  isApplyAllowed?: boolean;
  isRoutePlanningStatus?: boolean;
  /** The suggested route is already applied to the case. */
  isApplied?: boolean;
  routeType: RouteTypeSettingsEnum | null;
  cleanUpTimeMs?: number;
};

export const SuggestedRouteValidationProvider = ({
  suggestedRouteId,
  isApplyAllowed = false,
  isRoutePlanningStatus = false,
  isApplied = false,
  routeType,
  cleanUpTimeMs,
  children,
}: PropsWithChildren<SuggestedRouteValidationProviderProps>) => {
  const { status } = useBaseCaseDetails();
  const { legs: mainLegs, accommodationLegs: mainAccommodationLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { legs: dryRunLegs, accommodationLegs: dryRunAccommodationLegs } = useRouteDetails(RouteTypeEnum.DryRun);
  const { ClinicalSpecialist, OCSConsole } = useRouteResources();

  const [availabilityByPlan, registerAvailability] = useReducer(
    availabilityReducer,
    new Map<string, ResourceAvailability>(),
  );

  const setCSResource = useCallback((routeId: string, isAvailable: boolean) => {
    registerAvailability({ routeId, resource: 'cs', isAvailable });
  }, []);

  const setOCSResource = useCallback((routeId: string, isAvailable: boolean) => {
    registerAvailability({ routeId, resource: 'ocs', isAvailable });
  }, []);

  const setRSResource = useCallback((routeId: string, isAvailable: boolean) => {
    registerAvailability({ routeId, resource: 'rs', isAvailable });
  }, []);

  const isPartialRoute = !!routeType && PARTIAL_SUGGESTED_ROUTE_TYPES.includes(routeType);

  const hasUnremovableRouteLegs = useMemo(() => {
    const allMainLegs = [...mainLegs, ...mainAccommodationLegs];

    if (isPartialRoute) {
      const partialRouteLegs = getAffectedRouteLegs({
        isPartialRoute: true,
        mainRouteLegs: allMainLegs,
        dryRunRouteLegs: [],
        cleanUpTimeMs,
      });

      return (
        hasUnremovableLegs(partialRouteLegs, status) ||
        hasUnremovableLegs(getReplacedSegmentLegs(allMainLegs, routeType), status)
      );
    }

    const isDryRunReadOnly = isAllowedStatus({ statusFrom: CaseStatus.DeployedToTXCenter, currentStatus: status });

    return (
      hasUnremovableLegs(allMainLegs, status) ||
      hasUnremovableLegs([...dryRunLegs, ...dryRunAccommodationLegs], status, isDryRunReadOnly)
    );
  }, [
    isPartialRoute,
    routeType,
    mainLegs,
    mainAccommodationLegs,
    dryRunLegs,
    dryRunAccommodationLegs,
    status,
    cleanUpTimeMs,
  ]);

  const value = useMemo(() => {
    const availability = suggestedRouteId ? availabilityByPlan.get(suggestedRouteId) : undefined;

    const hasCSResource = suggestedRouteId ? !!availability?.cs : false;
    const hasOCSResource = suggestedRouteId ? !!availability?.ocs : false;
    const hasRSResource = suggestedRouteId ? (availability?.rs ?? true) : true;

    const hasResources = hasCSResource && hasOCSResource && hasRSResource;
    const blockingReason = getBlockingReason({
      isApplied,
      hasUnremovableRouteLegs,
      isRoutePlanningStatus,
      hasResources,
    });

    const regenerateErrorMessages = routeType
      ? getResourceValidationError(routeType, ClinicalSpecialist, OCSConsole)
      : [];

    return {
      isValidRoute: isApplyAllowed && !blockingReason,
      isRegenerateAllowed: !isPartialRoute || (!!ClinicalSpecialist.length && !!OCSConsole.length),
      regenerateErrorMessages,
      errorMessages: isApplyAllowed && blockingReason ? [blockingReason] : [],
      setCSResource,
      setOCSResource,
      setRSResource,
    };
  }, [
    suggestedRouteId,
    availabilityByPlan,
    hasUnremovableRouteLegs,
    isPartialRoute,
    isRoutePlanningStatus,
    isApplied,
    routeType,
    isApplyAllowed,
    setCSResource,
    setOCSResource,
    setRSResource,
    ClinicalSpecialist,
    OCSConsole,
  ]);

  return <SuggestedRouteValidationContext.Provider value={value}>{children}</SuggestedRouteValidationContext.Provider>;
};

export const useSuggestedRouteValidationContext = () => useContext(SuggestedRouteValidationContext);
